import React, { useEffect, useRef, useState } from 'react';
import Styles from '../../assets/css/components/logistic/logistic.module.scss';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BsThreeDotsVertical } from "react-icons/bs";
import { LiaEyeSolid } from "react-icons/lia";
import { BiEdit } from "react-icons/bi";
import { RiFilter2Line } from "react-icons/ri";
import { IoMdClose } from "react-icons/io";
import { AiOutlineSearch } from "react-icons/ai";
import Pagination from '../common/Pagination';
import { fetchLogistics, setFilters, resetFilters, setShouldRefresh } from '../../features/logistic/logisticSlice';
import Loader from '../../utils/Loader';

const Logistic = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { logistics, loading, error, pagination, filters, shouldRefresh } = useSelector((state) => state.logistic);

    const [isOpen, setIsOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [filterDate, setFilterDate] = useState({
        from: '',
        to: ''
    });

    const dropdownRefs = useRef({});
    const [dateError, setDateError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dropDownCompanyManage, setDropDownCompanyManage] = useState({
        id: 0,
        open: false
    });

    useEffect(() => {
        dispatch(fetchLogistics());
    }, [dispatch]);

    useEffect(() => {
        if (shouldRefresh) {
            dispatch(fetchLogistics());
            dispatch(setShouldRefresh(false));
        }
    }, [shouldRefresh, dispatch]);

    const dateStatusApply = () => {
        // Validate that both dates are provided if any is provided
        if ((filterDate.from && !filterDate.to) || (!filterDate.from && filterDate.to)) {
            setDateError('Both From and To dates are required');
            return;
        }

        dispatch(setFilters({
            ...filters,
            from: filterDate.from,
            to: filterDate.to,
            status: statusFilter,
            page: "1"
        }));
        dispatch(fetchLogistics());
        setIsOpen(false);
    };

    const handleReset = () => {
        setFilterDate({ from: '', to: '' });
        setStatusFilter('');
        setSearchTerm('');
        setDateError('');
        dispatch(resetFilters());
        dispatch(fetchLogistics());
        setIsOpen(false);
    };

    const handleSearch = (value) => {
        setSearchTerm(value);

        if (window.searchTimeout) {
            clearTimeout(window.searchTimeout);
        }

        window.searchTimeout = setTimeout(() => {
            dispatch(setFilters({
                ...filters,
                search: value,
                page: "1"
            }));
            dispatch(fetchLogistics());
        }, 500);
    };

    const handleDateChange = (type, value) => {
        setFilterDate(prev => ({
            ...prev,
            [type]: value
        }));
        setDateError('');
    };

    const handleStatusChange = (value) => {
        setStatusFilter(value);
    };

    const changePage = (count, type) => {
        if (type === "page") {
            dispatch(setFilters({ ...filters, page: count.toString() }));
            dispatch(fetchLogistics());
        }
        if (type === "show") {
            dispatch(setFilters({ ...filters, page: "1", paginate_per_page: count.toString() }));
            dispatch(fetchLogistics());
        }
    };

    const handleDropDown = (e) => {
        let value = e.currentTarget.dataset.userId;
        if (dropDownCompanyManage.id === value) {
            setDropDownCompanyManage({ ...dropDownCompanyManage, open: !dropDownCompanyManage.open });
        } else {
            setDropDownCompanyManage({ id: value, open: true });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '/');
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutside = !Object.values(dropdownRefs.current).some(ref =>
                ref && ref.contains(event.target)
            );

            if (isOutside) {
                setDropDownCompanyManage({ id: 0, open: false });
            }

            if (isOpen && !event.target.closest(`.${Styles.filter_dropdown}`)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    if (error) {
        return <div className={`${Styles.error}`}>Error: {error}</div>;
    }

    return (
        <div className={`${Styles.logistic}`}>
            <div className={`${Styles.logistic_heading}`}>
                <h2>Logistic Staff</h2>
                <button className={`${Styles.add_btn}`} onClick={() => navigate("/logistic/create")}>
                    Add Logistic Staff
                </button>
            </div>

            <div className={`${Styles.logistic_container}`}>
                {/* Filter And Search Data */}
                <div className={`${Styles.filter_search_btn}`}>
                    <div className={`${Styles.filter}`}>
                        <div className={`${Styles.sub_filter}`}>
                            <button onClick={() => setIsOpen(!isOpen)}>
                                <RiFilter2Line /> Filter
                            </button>
                            {isOpen && (
                                <div className={`${Styles.filter_dropdown}`}>
                                    <div className={`${Styles.heading}`}>
                                        <p>Filter</p>
                                        <IoMdClose onClick={() => setIsOpen(!isOpen)} />
                                    </div>

                                    <div className={`${Styles.selectDate}`}>
                                        <div className={`${Styles.heading_clear}`}>
                                            <p>Select Date Range</p>
                                            <p onClick={() => { setFilterDate({ from: '', to: '' }); setDateError(''); }}>Clear</p>
                                        </div>
                                        <div className={`${Styles.filter_date}`}>
                                            <div className={`${Styles.from}`}>
                                                <label htmlFor="from">From:</label>
                                                <input type="date" id='from' placeholder='00-00-0000' value={filterDate.from} max={filterDate.to || undefined} onChange={(e) => handleDateChange('from', e.target.value)} />
                                            </div>
                                            <div className={`${Styles.to}`}>
                                                <label htmlFor="to">To:</label>
                                                <input type="date" id='to' placeholder='00-00-0000' value={filterDate.to} min={filterDate.from || undefined} onChange={(e) => handleDateChange('to', e.target.value)} />
                                            </div>
                                        </div>
                                        {dateError && <div className={`${Styles.date_error}`} style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{dateError}</div>}
                                    </div>

                                    <div className={`${Styles.selectStatus}`}>
                                        <div className={`${Styles.heading_clear}`}>
                                            <p>Select Status</p>
                                            <p onClick={() => handleStatusChange('')}>Clear</p>
                                        </div>
                                        <select name="statusFilter" id="statusFilter" value={statusFilter} onChange={(e) => handleStatusChange(e.target.value)}>
                                            <option value="" hidden>**** Select Status ****</option>
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                    </div>

                                    <div className={`${Styles.buttons}`}>
                                        <button onClick={handleReset}>Reset</button>
                                        <button onClick={dateStatusApply}>Apply Now</button>
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`${Styles.search}`}>
                        <div className={`${Styles.inputContainer}`}>
                            <AiOutlineSearch className={`${Styles.searchIcon}`} />
                            <input
                                type="text"
                                placeholder="Search by Name"
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Item Table */}
                <div className={`${Styles.product}`}>
                    <div className={`${Styles.item_table}`}>
                        {loading ? (
                            <div className={`${Styles.loader_container}`}>
                                <Loader size="large" color="primary" />
                            </div>
                        ) : error ? (
                            <div className={`${Styles.error_message}`}>
                                {error}
                            </div>
                        ) : logistics.length === 0 ? (
                            <div className={`${Styles.no_data}`}>
                                No Logistics Staff found
                            </div>
                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        <th className={`${Styles.first_row}`}>ID</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Phone number</th>
                                        <th>Status</th>
                                        <th>Join date</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logistics.map((item) => (
                                        <tr key={item.id}>
                                            <td className={`${Styles.first_row}`}>{item.id}</td>
                                            <td>{item.name}</td>
                                            <td>{item.email}</td>
                                            <td>{item.phone_number}</td>
                                            <td className={`${Styles.status_badge} ${item.status === 1 ? Styles.active : Styles.inactive}`}>
                                                {item.status === 1 ? 'Active' : 'Inactive'}
                                            </td>
                                            <td>{formatDate(item.created_at)}</td>
                                            <td>
                                                <div className={`${Styles.manage}`} ref={el => dropdownRefs.current[item.id] = el}>
                                                    <BsThreeDotsVertical data-userId={item.id} onClick={handleDropDown} />
                                                    {dropDownCompanyManage.id == item.id && dropDownCompanyManage.open && (
                                                        <div className={`${Styles.manageUser}`}>
                                                            <p onClick={() => navigate(`/logistic/view/${item.id}`)}>
                                                                <span><LiaEyeSolid /></span>
                                                                <span>View</span>
                                                            </p>
                                                            <p onClick={() => navigate(`/logistic/edit/${item.id}`)}>
                                                                <span><BiEdit /></span>
                                                                <span>Edit</span>
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Pagination */}
                {logistics && logistics.length > 0 && (
                    <Pagination data={pagination} pageClick={changePage} />
                )}
            </div>
        </div>
    );
};

export default Logistic;